import { TZDateMini } from '@date-fns/tz';
import { useDatepicker } from '@datepicker-react/hooks';
import { DndContext } from '@dnd-kit/core';
import { useState, type FC, type ReactNode } from 'react';
import { dropdownContentClasses } from 'src/components/buttons-and-actions/dropdown-trigger';
import { VerticalDivider } from 'src/components/dividers';
import type {
  DayDroppableData,
  IsDayDragging,
} from 'src/components/form/datepicker/day';
import { GlobalDraggingStyle } from 'src/components/form/datepicker/global-dragging-style';
import { Month } from 'src/components/form/datepicker/month';
import {
  TimeSelector,
  useTimeValue,
} from 'src/components/form/datepicker/time-selector';
import { useCustomSensors } from 'src/components/form/datepicker/use-custom-sensors';
import type { InputTime } from 'src/components/form/datepicker/utils';
import {
  attachTime,
  checkBlockedDate,
} from 'src/components/form/datepicker/utils';
import type { DropdownState } from 'src/hooks/use-dropdown';
import { useNow } from 'src/hooks/use-now';
import { cn } from 'src/utils/cn';
import { timeZone } from 'src/utils/date-time-format';

type DatePopoverProps = {
  dropdownState: DropdownState;
  time: InputTime;
  handleSelect: (value: Date | null) => void;
  value: Date | null;
  minBookingDate?: Date;
  maxBookingDate?: Date;
  hint: ReactNode;
  error: ReactNode;
  isDateBlockedCustom?: (date: Date) => boolean;
};

export const DatePopover: FC<DatePopoverProps> = ({
  dropdownState,
  time,
  value,
  handleSelect,
  minBookingDate,
  maxBookingDate,
  hint,
  error,
  isDateBlockedCustom = () => false,
}) => {
  const now = new Date(useNow());
  const timeValue = useTimeValue(value);

  const {
    activeMonths,
    goToDate,
    goToPreviousMonthsByOneMonth,
    goToNextMonthsByOneMonth,
    isDateBlocked,
  } = useDatepicker({
    numberOfMonths: 1,
    startDate: value ?? now,
    endDate: null,
    focusedInput: null,
    onDatesChange: () => {},
    isDateBlocked(midnightInBerlin) {
      // date was already normalized before its passed to `useDay`,
      // so we know it's midnightInBerlin (but as UTC, so either 22:00 or 23:00).
      return (
        checkBlockedDate({
          midnightInBerlin,
          time,
          minBookingDate,
          maxBookingDate,
        }) || isDateBlockedCustom(new TZDateMini(midnightInBerlin, timeZone))
      );
    },
    // note: to avoid timezone specific errors, we need to handle minBookingDate/maxBookingDate
    // in our own dateBlockChecker
    // minBookingDate,
    // maxBookingDate,
  });

  const [targetDate, setTargetDate] = useState<Date | null>(null);
  const [isDayDragging, setIsDayDragging] = useState<IsDayDragging>(null);

  const sensors = useCustomSensors();

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => setIsDayDragging('single')}
      onDragOver={(event) => {
        if (!event.over) {
          setTargetDate(null);
          return;
        }
        const data = event.over.data.current as DayDroppableData;
        setTargetDate(data.date);
      }}
      onDragEnd={(event) => {
        setIsDayDragging(null);
        if (!event.over) return;
        const data = event.over.data.current as DayDroppableData;
        handleSelect(
          time
            ? attachTime({
                originalDate: data.date,
                hours: timeValue.value.hours,
                minutes: timeValue.value.minutes,
                time,
              })
            : data.date
        );
      }}
    >
      <GlobalDraggingStyle isDragging={Boolean(isDayDragging)} />
      <div
        ref={dropdownState.refs.setFloating}
        {...dropdownState.getFloatingProps()}
        style={{ ...dropdownState.style }}
        className={cn(dropdownContentClasses, 'max-w-min')}
        data-testid="date-popover"
      >
        <div className="bg-background-surface-default p-xl">
          <div className="grid grid-flow-col items-start gap-xl">
            {/* always one month actually */}
            {activeMonths.map((month) => (
              <Month
                key={`${month.year}-${month.month}`}
                year={month.year}
                month={month.month}
                date={month.date}
                startDate={value}
                endDate={value}
                isDayDragging={isDayDragging}
                targetDate={targetDate}
                onDayHover={() => {}}
                onChange={(date) => goToDate(date)}
                onNextMonthClick={goToNextMonthsByOneMonth}
                onPrevMonthClick={goToPreviousMonthsByOneMonth}
                isDateBlocked={isDateBlocked}
                onDateSelect={(date) =>
                  handleSelect(
                    time
                      ? attachTime({
                          originalDate: date,
                          hours: timeValue.value.hours,
                          minutes: timeValue.value.minutes,
                          time,
                        })
                      : date
                  )
                }
              />
            ))}

            {time && <VerticalDivider />}

            {time && (
              <TimeSelector
                timeValue={timeValue.value.timeString}
                onTimeChange={(timeString) => {
                  const [hours, minutes] = timeString.split(':') as [
                    string,
                    string,
                  ];
                  if (value)
                    handleSelect(
                      attachTime({
                        originalDate: value,
                        hours,
                        minutes,
                        time,
                      })
                    );
                  else
                    timeValue.setValue({
                      timeString,
                      hours,
                      minutes,
                    });
                }}
                time={time}
              />
            )}
          </div>

          {hint}
          {error && <div className="mt-md">{error}</div>}
        </div>
      </div>
    </DndContext>
  );
};
